import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { REDIRECT_QUERY_NAME } from '~/components/ModalDeliveryAddress/constants';
import { useModal } from '~/hooks/useModal';
import { ROUTES } from '~/pages/Root/constants';
import { cartStore, orderStore, userStore } from '~/stores';
import { getAddressModalType } from '~/utils/addressModal';
import {
  checkoutBlocker,
  reportCheckoutBlocked,
} from '~/utils/checkoutBlocker';

interface UseNavigateCheckoutProps {
  /** Opening source reported by the address modal. */
  source?: string;
  /**
   * Runs after the address gate passes and before navigating. Return `true`
   * when the cart changed under the user, which cancels the transition.
   */
  hasCartChanged?: () => Promise<boolean>;
}

/**
 * Sends the user to the checkout, but only once it can actually serve them:
 * an address has to be selected, and a warehouse has to deliver to it. Neither
 * holding, the address modal opens instead — carrying a redirect that resumes
 * the transition as soon as a usable address is saved.
 */
const useNavigateCheckout = (props?: UseNavigateCheckoutProps) => {
  const { source, hasCartChanged } = props || {};
  const navigate = useNavigate();
  const { openModal } = useModal();

  return useCallback(async () => {
    const blocker = checkoutBlocker();

    if (blocker) {
      reportCheckoutBlocked(blocker, {
        source: source ?? null,
        addressId: userStore.deliveryAddress?.addressId ?? null,
        addressListLength: userStore.addressList.length,
        isAuthorized: userStore.isAuthorized,
        cartItemsCount: cartStore.cart.length,
        warehouseCode: orderStore.etaCalculation?.warehouse.code ?? null,
      });

      openModal(getAddressModalType(), source, {
        [REDIRECT_QUERY_NAME]: ROUTES.checkout,
      });
      return;
    }

    try {
      if (await hasCartChanged?.()) {
        return;
      }
    } catch {
      // The cart could not be re-calculated; handleCalculateError has already
      // turned the reason into UI, so stay on the cart instead of walking into
      // a checkout built on stale totals.
      return;
    }

    navigate(ROUTES.checkout);
  }, [source, hasCartChanged, navigate, openModal]);
};

export default useNavigateCheckout;
